import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CalendarDays, ChevronRight, CheckCircle2, Circle, FileText, ListChecks, User, Mail, Globe,
} from "lucide-react";
import { getPortalMe } from "../../application/portalAccount";
import { listPortalAgreements, listPortalSessions } from "../../application/portalRecords";
import { useAgreementUpdates, type AgreementFailure } from "../../application/useAgreementUpdates";
import BookSessionSection from "../components/BookSessionSection";
import NextSessionCard from "../components/NextSessionCard";
import PendingPaymentsNotice from "../components/PendingPaymentsNotice";
import WhatsAppRemindersCard from "../components/WhatsAppRemindersCard";
import type { PortalAgreement, PortalMe, PortalSession, PortalTherapist } from "../../domain/types";
import {
  agreementProgress,
  groupAgreementsBySession,
  openAgreements,
  type OpenAgreement,
} from "../../domain/sessionAgreements";
import { formatDay } from "../../domain/dates";
import { formatTherapyTypes } from "../../../../shared/domain/therapyTypes";

const RECENT_SESSIONS = 5;
const OPEN_AGREEMENTS_SHOWN = 5;

type LoadState = "loading" | "failed" | "ready";

/**
 * Where a patient lands between sessions.
 *
 * Ordered by what they come to do: see when the next session is and get into
 * it, then what they agreed to work on, then the rest. Counters of sessions
 * and notes used to lead the page; they answered nothing a patient asks.
 */
export default function PortalDashboard() {
  const { t } = useTranslation();
  const [me, setMe] = useState<PortalMe | null>(null);
  const [sessions, setSessions] = useState<PortalSession[]>([]);
  const [agreementsFailed, setAgreementsFailed] = useState(false);
  // The agreements open when the page loaded. One ticked off stays in the
  // list, done, so the tap visibly lands and can be taken back.
  const [shownIds, setShownIds] = useState<string[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  // Bumped on each booking so the next-session card reads it again.
  const [bookings, setBookings] = useState(0);
  const { agreements, setAgreements, changeStatus, failure } = useAgreementUpdates();

  const load = useCallback(() => {
    setLoadState("loading");
    Promise.all([
      getPortalMe(),
      listPortalSessions(),
      // Agreements failing leaves their card empty, not the whole dashboard.
      listPortalAgreements().catch((): PortalAgreement[] | null => null),
    ])
      .then(([meData, sessionsData, agreementsData]) => {
        setMe(meData);
        setSessions(sessionsData);
        setAgreementsFailed(agreementsData === null);
        setAgreements(agreementsData ?? []);
        const open = openAgreements(groupAgreementsBySession(sessionsData, agreementsData ?? []));
        setShownIds(open.map(({ agreement }) => agreement.id));
        setLoadState("ready");
      })
      .catch(() => setLoadState("failed"));
  }, [setAgreements]);

  useEffect(load, [load]);

  if (loadState === "loading") return <DashboardSkeleton />;

  if (loadState === "failed") {
    return (
      <div role="alert" className="glass-effect glass-border mx-auto mt-10 max-w-md rounded-2xl border p-8 text-center">
        <p className="text-sm text-content-secondary">{t("portal.dashboard.loadError")}</p>
        <button
          type="button"
          onClick={load}
          className="mt-4 min-h-11 rounded-xl bg-brand-600 px-5 text-sm font-medium text-white hover:bg-brand-700"
        >
          {t("portal.dashboard.retry")}
        </button>
      </div>
    );
  }

  const groups = groupAgreementsBySession(sessions, agreements);
  const sessionById = new Map(sessions.map((session) => [session.id, session]));
  const shown: OpenAgreement[] = agreements
    .filter((agreement) => shownIds.includes(agreement.id))
    .sort((a, b) => shownIds.indexOf(a.id) - shownIds.indexOf(b.id))
    .flatMap((agreement) => {
      const session = sessionById.get(agreement.session_id);
      return session ? [{ agreement, session }] : [];
    });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-content-primary">
          {t("portal.dashboard.greeting", { name: me?.first_name ?? "" })}
        </h1>
        <p className="text-sm text-content-tertiary mt-1">
          {t("portal.dashboard.subtitle")}
        </p>
      </div>

      {/* Renders nothing unless the therapist enabled Payments and something
          is actually outstanding. */}
      <PendingPaymentsNotice />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* What is coming, then how to arrange more. Both render nothing
              when the therapist has no Scheduler. */}
          <NextSessionCard key={bookings} />

          <OpenAgreementsCard
            items={shown.slice(0, OPEN_AGREEMENTS_SHOWN)}
            hiddenCount={Math.max(0, shown.length - OPEN_AGREEMENTS_SHOWN)}
            progress={agreementProgress(groups)}
            unavailable={agreementsFailed}
            failure={failure}
            onToggle={(agreement) =>
              changeStatus(agreement, agreement.status === "completed" ? "pending" : "completed")
            }
          />

          <BookSessionSection onBooked={() => setBookings((n) => n + 1)} />

          <RecentSessions sessions={sessions} />
        </div>

        <aside className="flex flex-col gap-6">
          {me?.therapist && <TherapistCard therapist={me.therapist} />}
          <DiaryCard />
          {/* One switch, one home. It hides itself when the deployment has no
              WhatsApp, and appears whether or not any tool is adopted, because
              consent already given must always be withdrawable. */}
          <WhatsAppRemindersCard />
        </aside>
      </div>
    </>
  );
}

function DashboardSkeleton() {
  const block = "animate-pulse rounded-2xl bg-gray-200/70 dark:bg-white/5";
  return (
    <div aria-busy="true" className="flex flex-col gap-6">
      <div className={`${block} h-8 w-48`} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <div className={`${block} h-40`} />
          <div className={`${block} h-56`} />
        </div>
        <div className={`${block} h-48`} />
      </div>
    </div>
  );
}

const CARD = "glass-effect glass-border border rounded-2xl shadow-sm";

function OpenAgreementsCard({
  items,
  hiddenCount,
  progress,
  unavailable,
  failure,
  onToggle,
}: {
  items: OpenAgreement[];
  hiddenCount: number;
  progress: { completed: number; total: number };
  unavailable: boolean;
  failure: AgreementFailure | null;
  onToggle: (agreement: PortalAgreement) => void;
}) {
  const { t, i18n } = useTranslation();

  if (unavailable || progress.total === 0) {
    return (
      <section className={`${CARD} p-5 text-center`}>
        <ListChecks className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" aria-hidden="true" />
        <p className="text-sm text-content-tertiary">{t("portal.agreements.empty")}</p>
      </section>
    );
  }

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-white/5">
        <ListChecks className="w-4 h-4 text-brand-500" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-content-primary">
          {t("portal.dashboard.openAgreements")}
        </h2>
        <span className="text-xs text-content-tertiary">
          {t("portal.agreements.progress", progress)}
        </span>
        <Link
          to="/portal/agreements"
          className="ml-auto flex min-h-11 items-center text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          {t("portal.dashboard.viewAll")}
        </Link>
      </div>

      {items.length === 0 ? (
        <p className="flex items-center gap-2 px-5 py-6 text-sm text-content-secondary">
          <CheckCircle2 className="w-5 h-5 text-status-success" aria-hidden="true" />
          {t("portal.dashboard.agreementsUpToDate")}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-white/5">
          {items.map(({ agreement, session }) => {
            const done = agreement.status === "completed";
            return (
              <li key={agreement.id} className="flex items-start gap-1 px-2 py-2 sm:px-3">
                <button
                  type="button"
                  onClick={() => onToggle(agreement)}
                  aria-label={t(done ? "portal.agreements.markPending" : "portal.agreements.markComplete")}
                  className={`flex size-11 shrink-0 items-center justify-center rounded-full transition-colors ${
                    done
                      ? "text-status-success"
                      : "text-gray-300 hover:text-brand-500 dark:text-gray-600"
                  }`}
                >
                  {done ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
                </button>
                <div className="min-w-0 flex-1 py-2.5">
                  <p className={`text-sm leading-relaxed ${done ? "line-through text-content-tertiary" : "text-content-primary"}`}>
                    {agreement.text}
                  </p>
                  <p className="mt-0.5 text-xs text-content-tertiary truncate">
                    {formatDay(session.date, i18n.language)} · {session.title}
                  </p>
                  {failure?.agreementId === agreement.id && (
                    <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {t("portal.agreements.saveFailed")}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {hiddenCount > 0 && (
        <Link
          to="/portal/agreements"
          className="flex min-h-11 items-center justify-center border-t border-gray-100 text-xs font-medium text-brand-600 hover:bg-surface-sunken dark:border-white/5 dark:text-brand-400"
        >
          {t("portal.dashboard.moreOpen", { count: hiddenCount })}
        </Link>
      )}
    </section>
  );
}

function RecentSessions({ sessions }: { sessions: PortalSession[] }) {
  const { t } = useTranslation();
  const recent = sessions.slice(0, RECENT_SESSIONS);

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-content-primary">
          <CalendarDays className="w-4 h-4 text-brand-500" aria-hidden="true" />
          {t("portal.dashboard.recentSessions")}
        </h2>
        {sessions.length > RECENT_SESSIONS && (
          <span className="text-xs text-content-tertiary">
            {t("portal.dashboard.showingRecent", { count: RECENT_SESSIONS })}
          </span>
        )}
      </div>

      {recent.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <CalendarDays className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm text-content-tertiary">{t("portal.dashboard.noSessions")}</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-white/5">
          {recent.map((session) => (
            <SessionRow key={session.id} session={session} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SessionRow({ session }: { session: PortalSession }) {
  // Its own, like every other component in this file: the dashboard's `t` is
  // not in scope here, and reaching for it crashed the whole portal the first
  // time a session carried a therapy type.
  const { t, i18n } = useTranslation();

  return (
    <li>
      <Link
        to={`/portal/agreements?session=${session.id}`}
        className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors group"
      >
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-sm font-medium text-content-primary truncate">
            {session.title}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-content-tertiary">
              {formatDay(session.date, i18n.language)}
            </span>
            {session.therapy_type && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="text-xs text-content-tertiary">
                  {formatTherapyTypes(t, session.therapy_type)}
                </span>
              </>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-500 transition-colors shrink-0 ml-3" />
      </Link>
    </li>
  );
}

function DiaryCard() {
  const { t } = useTranslation();
  return (
    <section className={`${CARD} p-5`}>
      <h2 className="flex items-center gap-2 text-sm font-semibold text-content-primary">
        <FileText className="w-4 h-4 text-brand-500" aria-hidden="true" />
        {t("portal.notes.title")}
      </h2>
      <p className="mt-2 text-sm text-content-tertiary">{t("portal.dashboard.notesDesc")}</p>
      <Link
        to="/portal/notes"
        className="mt-4 flex min-h-11 items-center justify-center rounded-xl border border-brand-200 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-500/30 dark:text-brand-300 dark:hover:bg-brand-500/10"
      >
        {t("portal.dashboard.openDiary")}
      </Link>
    </section>
  );
}

function TherapistCard({ therapist }: { therapist: PortalTherapist }) {
  const { t } = useTranslation();
  const initials = `${therapist.first_name[0] ?? ""}${therapist.last_name[0] ?? ""}`.toUpperCase();

  return (
    <div className="glass-effect glass-border border rounded-2xl shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <User className="w-4 h-4 text-brand-500" />
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {t("portal.dashboard.therapist")}
        </h2>
      </div>
      <div className="flex items-center gap-3 mb-4">
        {/* The face where there is one to show, initials where there is not:
            a patient reading this between sessions is looking for the person
            they sit with, and two letters is what we have when the therapist
            has not published a photo. */}
        {therapist.photo_url ? (
          <img
            src={therapist.photo_url}
            alt=""
            loading="lazy"
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{initials}</span>
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-content-primary truncate">
            {therapist.first_name} {therapist.last_name}
          </p>
          {therapist.description && (
            <p className="text-xs text-content-tertiary truncate">{therapist.description}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-content-tertiary">
          <Mail className="w-3.5 h-3.5 shrink-0 text-content-tertiary" />
          <span className="truncate">{therapist.email}</span>
        </div>
        {therapist.country && (
          <div className="flex items-center gap-2 text-xs text-content-tertiary">
            <Globe className="w-3.5 h-3.5 shrink-0 text-content-tertiary" />
            <span>{therapist.country}</span>
          </div>
        )}
        {!therapist.country && (
          <div className="flex items-center gap-2 text-xs text-content-tertiary">
            <User className="w-3.5 h-3.5 shrink-0 text-content-tertiary" />
            <span>{t("portal.dashboard.therapistContactNote")}</span>
          </div>
        )}
      </div>
    </div>
  );
}
